import type {
  BestPriceQuery,
  BestPriceResult,
  RoomPriceQuery,
  RoomPriceResult,
  WidgetConfig,
} from './types';
import { t, type TranslationKeys } from './i18n';

const DEFAULT_ADULT_AGE = 30;

/**
 * Le endpoint `/booking/*` attend `residents` sous forme de liste d'âges
 * séparés par des virgules (ex. "30,30" pour 2 adultes), pas un compte.
 * `bestprice` est tolérant car l'API Reservit renvoie un prix générique,
 * mais `roomprice` (avec roomtcode/catcode) interprète strictement les
 * âges et renvoie -1 si la config ne correspond pas.
 */
function residentsParam(count: number): string {
  const n = Math.max(1, Math.floor(count));
  return Array.from({ length: n }, () => String(DEFAULT_ADULT_AGE)).join(',');
}

export async function fetchBestPrice(
  cfg: WidgetConfig,
  q: BestPriceQuery,
  signal?: AbortSignal,
): Promise<BestPriceResult> {
  const i18n: TranslationKeys = t(cfg.lang);
  const url = new URL('/booking/bestprice', cfg.apiUrl);
  url.searchParams.set('startdate', q.startdate);
  url.searchParams.set('enddate', q.enddate);
  url.searchParams.set('residents', residentsParam(q.residents));
  url.searchParams.set('lang', cfg.lang);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${cfg.token}`,
      Accept: 'application/json',
    },
    signal,
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error(i18n.invalidToken);
  }
  if (!res.ok) {
    throw new Error(i18n.serviceUnavailable(res.status));
  }

  const data = (await res.json()) as BestPriceResult[] | BestPriceResult;
  const first = Array.isArray(data) ? data[0] : data;
  if (!first) {
    throw new Error(i18n.noData);
  }
  return first;
}

export function buildRedirectUrl(cfg: WidgetConfig, q: BestPriceQuery): string {
  const url = new URL('/booking/redirect', cfg.apiUrl);
  url.searchParams.set('startdate', q.startdate);
  url.searchParams.set('enddate', q.enddate);
  url.searchParams.set('residents', residentsParam(q.residents));
  url.searchParams.set('lang', cfg.lang);
  if (cfg.token) {
    url.searchParams.set('access_token', cfg.token);
  }
  return url.toString();
}

export async function fetchRoomPrice(
  cfg: WidgetConfig,
  q: RoomPriceQuery,
  signal?: AbortSignal,
): Promise<RoomPriceResult> {
  const i18n: TranslationKeys = t(cfg.lang);
  const url = new URL('/booking/roomprice', cfg.apiUrl);
  url.searchParams.set('startdate', q.startdate);
  url.searchParams.set('enddate', q.enddate);
  url.searchParams.set('residents', residentsParam(q.residents));
  url.searchParams.set('lang', cfg.lang);
  if (q.idRoom) url.searchParams.set('id_room', q.idRoom);
  if (q.roomtcode) url.searchParams.set('roomtcode', q.roomtcode);
  if (q.catcode) url.searchParams.set('catcode', q.catcode);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${cfg.token}`,
      Accept: 'application/json',
    },
    signal,
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error(i18n.invalidToken);
  }
  if (!res.ok) {
    throw new Error(i18n.serviceUnavailable(res.status));
  }

  const data = (await res.json()) as RoomPriceResult[] | RoomPriceResult;
  const list = Array.isArray(data) ? data : data ? [data] : [];
  if (list.length === 0) {
    throw new Error(i18n.noData);
  }
  // Sites multi-moteurs : on prend l'offre avec le prix le plus bas et > 0,
  // sinon on retombe sur la première entrée (qui sera "indisponible").
  const valid = list
    .filter((r) => typeof r.bestPrice === 'number' && r.bestPrice > 0)
    .sort((a, b) => a.bestPrice - b.bestPrice);
  return valid[0] ?? list[0];
}

export function buildRoomRedirectUrl(
  cfg: WidgetConfig,
  q: RoomPriceQuery,
): string {
  const url = new URL('/booking/redirect', cfg.apiUrl);
  url.searchParams.set('startdate', q.startdate);
  url.searchParams.set('enddate', q.enddate);
  url.searchParams.set('residents', residentsParam(q.residents));
  url.searchParams.set('lang', cfg.lang);
  if (q.idRoom) url.searchParams.set('id_room', q.idRoom);
  if (q.roomtcode) url.searchParams.set('roomtcode', q.roomtcode);
  if (q.catcode) url.searchParams.set('catcode', q.catcode);
  if (cfg.token) {
    url.searchParams.set('access_token', cfg.token);
  }
  return url.toString();
}

export function formatPrice(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}
